import { ChangeEvent, FC, useState, useEffect, useMemo, useRef } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { alpha } from "@mui/material/styles";
import { usePayers, usePayerDependencies } from "../../hooks/userPayers";
import {
	useCreatePayer,
	useUpdatePayer,
	useDeletePayer,
} from "../../hooks/userPayers/useMutatePayer";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import Tooltip from "@mui/material/Tooltip";
import { PayerResponse, CreatePayer } from "@/domain/payer.interface";
import { useUserStore } from "../../stores/user.store";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";
import { useCreateAuditlog } from "../../hooks/useAuditlog";
import AuditLogModal from "../../components/modals/AuditLog";
import ConfigListToolbar, {
	ConfigListViewControls,
} from "../../components/ConfigListShell/ConfigListToolbar";
import ConfigTable, {
	type ConfigColumn,
} from "../../components/ConfigListShell/ConfigTable";
import ConfigCards from "../../components/ConfigListShell/ConfigCards";
import PayerEditor from "./PayerEditor";
import {
	categoryLabel as payerCategoryLabel,
	filterableCategories,
} from "./payerCategories";
import { usePayerCategories } from "../../hooks/useCategories";
import ConfigRowActions from "../../components/ConfigListShell/ConfigRowActions";
import ConfigDeleteDialog, {
	type DeleteDependency,
} from "../../components/ConfigListShell/ConfigDeleteDialog";
import ConfigListStates, {
	type ListState,
} from "../../components/ConfigListShell/ConfigListStates";
import { useConfigList } from "../../components/ConfigListShell/useConfigList";
import { C_BTN } from "../../components/ConfigListShell/buttonMetrics";

/**
 * How long a search term must settle before it is written to the audit log. The previous
 * implementation posted on every keystroke (SIMS-8594) — see the effect below.
 */
const SEARCH_AUDIT_DEBOUNCE_MS = 800;

/** AC2 — the fields search runs over: name, description and the member-ID pattern. */
const searchablePayerFields = (payer: PayerResponse) => [
	payer.name,
	payer.description,
	payer.regularExpression,
];

const payerStatus = (payer: PayerResponse) => payer.status;

const payerSortValue = (payer: PayerResponse, key: string) =>
	(payer as unknown as Record<string, unknown>)[key];

/** Pager chevrons — design v2 L293-296: 32px square, 8px radius, 1px --border-default. */
const PAGER_BTN = {
	width: "2rem",
	height: "2rem",
	borderRadius: "0.5rem",
	border: "1px solid",
	borderColor: "divider",
};

const STATUS_OPTIONS = [
	{ value: "all", label: "All" },
	{ value: "ACTIVE", label: "Active" },
	{ value: "INACTIVE", label: "Inactive" },
];

interface ApiError {
	response?: {
		data?: {
			message?: string | string[];
		};
	};
	message?: string;
}

const PayersComponent: FC = () => {
	const [editorOpen, setEditorOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [selectedPayer, setSelectedPayer] = useState<PayerResponse | null>(
		null,
	);
	const [openAuditLogModal, setOpenAuditLogModal] = useState(false);

	const { userInfo } = useUserStore();
	const { showSnackBar } = useFeedBack();
	const createAuditlogMutation = useCreateAuditlog();
	const postAuditlogCalledRef = useRef(false);

	const {
		data: PayersResponse,
		isLoading: payersLoading,
		error: payersError,
		refetch: refetchPayers,
	} = usePayers();

	// SIMS-8647 AC3 — programs are countable exactly on the frontend: Program.payers is a
	// string[] of payer ids. NOT filtered by program status: C17519 requires a payer configured
	// only on an INACTIVE program to still block deletion.

	// SIMS-8634 — one fetch serves the filter select, the list column and the card chips.
	const { data: categoryDoc } = usePayerCategories();
	const categoryItems = useMemo(
		() => categoryDoc?.categories ?? [],
		[categoryDoc],
	);
	const categoryById = useMemo(
		() => new Map(categoryItems.map((c) => [c._id, c])),
		[categoryItems],
	);
	/** The chips a payer carries. Retired ones stay shown (AC5), marked as such. */
	const payerCategoryChips = (payer: PayerResponse) =>
		(payer.categories ?? [])
			.map((id) => categoryById.get(id))
			.filter((c): c is NonNullable<typeof c> => c !== undefined);

	// SIMS-8647 — fetched only while the confirmation is open (decision 2026-08-31): the counts are
	// needed for ONE payer at the moment deletion is asked for, not for every row.
	const {
		data: dependencyCounts,
		isFetching: isCheckingDependencies,
		isError: dependenciesUnavailable,
	} = usePayerDependencies(selectedPayer?._id ?? null, deleteDialogOpen);

	const deleteDependencies: DeleteDependency[] = useMemo(() => {
		if (!selectedPayer || !dependencyCounts) {
			return [];
		}
		const deps: DeleteDependency[] = [];
		// Programs and members both come from the service now (GET /payers/:id/dependencies),
		// rather than being counted from the already-fetched list, which would disagree with the
		// service whenever the list is stale.
		//
		// NOTE — this blocking is UI-ONLY. An earlier version of this comment claimed "the service
		// is the one that refuses the delete"; it is not. DELETE /payers/:id calls
		// BaseService.delete straight through, which only sets isDeleted — countDependencies is
		// reached solely by GET /:id/dependencies. A direct DELETE with the PAYER_DELETE grant
		// still removes a payer that has live payer-plan schedule entries. That is TestRail C17517
		// ("cannot be deleted by a direct request while members are registered"), which is NOT
		// implemented.
		if (dependencyCounts.programs > 0) {
			deps.push({
				kind: "programs",
				count: dependencyCounts.programs,
				label: "programs",
			});
		}
		// AC2: registered members block deletion. Previously absent — the endpoint did not exist
		// when this screen was built, so a payer with members and no programs showed NO
		// dependencies and was offered for deletion.
		if (dependencyCounts.members > 0) {
			deps.push({
				kind: "members",
				count: dependencyCounts.members,
				label: "registered members",
			});
		}
		return deps;
	}, [selectedPayer, dependencyCounts]);

	const createMutation = useCreatePayer();
	const updateMutation = useUpdatePayer();
	const deleteMutation = useDeletePayer();

	/**
	 * Record an audit entry. NEVER throws.
	 *
	 * The audit write is a SEPARATE call that happens after the entity has already been saved, so
	 * its failure says nothing about whether the user's change succeeded. Letting it reject meant a
	 * saved payer reported "Payer wasn't saved" and the editor stayed open holding input that was
	 * already persisted — the user's next move is to press save again and create a duplicate.
	 *
	 * That is not hypothetical: the six WEB_PAYER_* codes were missing from the backend's
	 * ActionCode enum for eight months, so every one of these calls returned 400 while the payer
	 * itself saved fine.
	 *
	 * Failing silently for the USER is right; failing silently for OPERATORS is not — an audit gap
	 * is a compliance concern, so the error still reaches the console for collection.
	 */
	const postAuditlog = async ({
		action,
		actionDetail,
		actionCode,
	}: {
		action: string;
		actionDetail: string;
		actionCode: string;
	}) => {
		// No `createdBy`: SIMS-7918 (e9a53e40, on development) stopped the client sending the acting
		// user on audit-log writes — the server derives it from the authenticated principal, so a
		// client-supplied value is at best ignored and at worst spoofable. The epic's branch still
		// sent it because it was cut before that landed.
		try {
			await createAuditlogMutation.mutateAsync({
				appType: "WEB_BACK_OFFICE",
				module: "Payers",
				action,
				detail: actionDetail,
				actionCode,
				option: "Payers Management",
			});
		} catch (error) {
			// The audit write is secondary to the operation the user asked for: a failed log must not
			// present a successful save as failed. Logged, not swallowed silently.
			// eslint-disable-next-line no-console
			console.error(
				`[Payers] audit log failed (${actionCode}) — the payer operation itself succeeded`,
				error,
			);
		}
	};

	useEffect(() => {
		if (!postAuditlogCalledRef.current) {
			postAuditlog({
				action: "When entering",
				actionDetail: "Viewed the payers management page",
				actionCode: "WEB_PAYER_LIST_VIEW",
			});
			postAuditlogCalledRef.current = true;
		}
	}, []);

	const payers = PayersResponse || [];

	// SIMS-8594 — the shared Pattern v2 list state: search, status filter, sort, view and
	// pagination. Client-side, because the service already fetches the whole collection, so
	// AC2/AC4's "across the whole record set" holds over the full list rather than a page.
	const list = useConfigList<PayerResponse>({
		rows: payers,
		searchableFields: searchablePayerFields,
		statusOf: payerStatus,
		categoriesOf: (payer) => payer.categories ?? [],
		sortValue: payerSortValue,
		defaultSortKey: "name",
	});

	// SIMS-8594 — one audit entry per settled search term, not one per keystroke. The previous
	// effect fired on every `search` change, so typing "Medicaid" wrote eight WEB_PAYER_LIST_SEARCH
	// rows and buried the real configuration changes the log exists to record.
	const auditedQueryRef = useRef("");
	useEffect(() => {
		const term = list.query.trim();
		if (term === "" || term === auditedQueryRef.current) {
			return;
		}
		const timer = setTimeout(() => {
			auditedQueryRef.current = term;
			postAuditlog({
				action: "When searching",
				actionDetail: "Searched within payers list",
				actionCode: "WEB_PAYER_LIST_SEARCH",
			});
		}, SEARCH_AUDIT_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [list.query]);

	const handleOpenDialog = (payer?: PayerResponse) => {
		setSelectedPayer(payer || null);
		setEditorOpen(true);
	};

	/**
	 * The status pill. `.c-pill` from the design system (card-system.css:268-278) —
	 * 12px / 500 / line-height 1, `-light` background with `-dark` text. Those values were
	 * already established by SIMS-8881; reused rather than re-derived.
	 *
	 * NOT the "ink tokens" hand-off §4 asks for: `--warning-ink` / `--success-ink` exist neither
	 * in the design system nor in this codebase, and `.c-pill--success` itself uses
	 * `--success-dark`. Raised as OQ-13; TestRail C17609 step 7 carries the same error.
	 *
	 * NFR-5: the label is text, so status is never conveyed by colour alone.
	 */
	const statusChip = (payer: PayerResponse) => (
		<Chip
			size="small"
			label={payer.status === "ACTIVE" ? "Active" : "Inactive"}
			color={payer.status === "ACTIVE" ? "success" : "default"}
			sx={{
				height: "1.5rem",
				borderRadius: 999,
				fontSize: "0.75rem",
				fontWeight: 500,
				lineHeight: 1,
			}}
		/>
	);

	/**
	 * SIMS-8602 — which of the four list states the list area is in. Order matters: a slow fetch
	 * must never read as "no payers" (the previous screen's bug), and a genuinely empty catalog
	 * must never read as "no search results" (AC3's "visibly different").
	 */
	const listState: ListState = payersLoading
		? "loading"
		: payersError
			? "load-failed"
			: payers.length === 0
				? "empty"
				: list.total === 0
					? "no-results"
					: "ready";

	const stateCopy = {
		loadFailedTitle: "Payers couldn't be loaded",
		loadFailedBody:
			"The request failed before any payer was returned. Nothing has been changed.",
		emptyTitle: "No payers yet",
		emptyBody:
			"Payers define which member IDs are valid at registration. Add the first payer to start registering members.",
		emptyIcon: "account_balance",
		loadingLabel: "Loading payers…",
		noResultsBody:
			"No payer name, description or pattern contains that text with the current filters.",
		noResultsTitle: (term: string) => `No payers match \u201C${term}\u201D`,
		addLabel: "Add payer",
	};

	/**
	 * The pager (design v2). Shared by both views, but trimmed differently:
	 *   table (L280): padding 10px 16px on --surface-0, INSIDE the white frame
	 *   cards (L357): padding 10px 24px on --surface-1, on the page below a transparent
	 *                 scroll area — the top border is the only thing separating it
	 */
	const renderPager = ({ inFrame }: { inFrame: boolean }) => (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 2,
				px: inFrame ? 2 : 3,
				py: 1.25,
				borderTop: "1px solid",
				borderTopColor: "divider",
				backgroundColor: inFrame ? "background.paper" : "transparent",
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
					Rows per page
				</Typography>
				<Box
					component="select"
					aria-label="Rows per page"
					value={list.pageSize}
					onChange={(e: ChangeEvent<HTMLSelectElement>) =>
						list.setPageSize(e.target.value)
					}
					sx={{
						height: "2rem",
						border: "1px solid",
						borderColor: "divider",
						borderRadius: "0.5rem",
						padding: "0 0.5rem",
						fontSize: "0.8125rem",
						backgroundColor: "background.paper",
						color: "text.primary",
					}}
				>
					{list.pageSizeOptions.map((o) => (
						<option key={o} value={o}>
							{o === "all" ? "All" : o}
						</option>
					))}
				</Box>
			</Box>

			<Box sx={{ flex: 1 }} />

			<Typography
				sx={{ fontSize: "0.75rem", color: "text.secondary" }}
				data-testid="payers-page-label"
			>
				{`${list.total === 0 ? 0 : list.rangeStart}\u2013${list.rangeEnd} of ${list.total}`}
			</Typography>

			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
				<IconButton
					aria-label="Previous page"
					size="small"
					disabled={list.page === 0}
					onClick={() => list.setPage(list.page - 1)}
					sx={PAGER_BTN}
				>
					<ChevronLeftIcon sx={{ fontSize: "1.125rem" }} />
				</IconButton>
				<IconButton
					aria-label="Next page"
					size="small"
					disabled={list.page >= list.pageCount - 1}
					onClick={() => list.setPage(list.page + 1)}
					sx={PAGER_BTN}
				>
					<ChevronRightIcon sx={{ fontSize: "1.125rem" }} />
				</IconButton>
			</Box>
		</Box>
	);

	const columns: ConfigColumn<PayerResponse>[] = [
		{
			key: "name",
			header: "Payer",
			width: "1.7fr",
			sortable: true,
			render: (payer) => payer.name,
		},
		{
			key: "regularExpression",
			header: "Member ID pattern",
			width: "1.4fr",
			sortable: true,
			render: (payer) => (
				<Box
					component="span"
					sx={{
						fontFamily: "'SF Mono', Menlo, monospace",
						fontSize: "0.8125rem",
					}}
				>
					{payer.regularExpression}
				</Box>
			),
		},
		{
			key: "status",
			header: "Status",
			width: "120px",
			sortable: true,
			render: statusChip,
		},
		{
			key: "autogenerated",
			header: "Member IDs",
			width: "110px",
			sortable: true,
			render: (payer) =>
				payer.autogenerated === "Y" ? "Autogenerated" : "Manual",
		},
		{
			// Design L236: column 5 is "Payer category". Description was the stand-in while the
			// field did not exist.
			key: "categories",
			header: "Payer category",
			width: "1.1fr",
			sortable: false,
			render: (payer) => {
				const chips = payerCategoryChips(payer);
				if (chips.length === 0) {
					return null;
				}
				return (
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
						{chips.map((cat) => (
							<Box
								key={cat._id}
								component="span"
								sx={{
									fontSize: "0.6875rem",
									fontWeight: 500,
									padding: "2px 8px",
									borderRadius: "999px",
									backgroundColor: (t) => t.palette.surfaceTwo.main,
									color: "text.secondary",
									border: "1px",
									borderStyle: cat.status === "INACTIVE" ? "dashed" : "solid",
									borderColor: "divider",
									opacity: cat.status === "INACTIVE" ? 0.6 : 1,
								}}
							>
								{payerCategoryLabel(cat)}
							</Box>
						))}
					</Box>
				);
			},
		},
		{
			key: "actions",
			header: "",
			width: "56px",
			sortable: false,
			align: "right",
			// A KEBAB, per the design (L262-275) — not a strip of icon buttons. Same component as
			// the card view uses, so the two views act identically.
			render: (payer) => (
				<Box display="flex" justifyContent="flex-end">
					<ConfigRowActions
						recordName={payer.name}
						actions={[
							{
								label: "Edit payer",
								icon: <EditIcon sx={{ fontSize: "1.125rem" }} />,
								onClick: () => handleOpenDialog(payer),
							},
							{
								label: "Delete",
								icon: <DeleteIcon sx={{ fontSize: "1.125rem" }} />,
								onClick: () => handleOpenDeleteDialog(payer),
								destructive: true,
							},
						]}
					/>
				</Box>
			),
		},
	];

	const handleCloseDialog = () => {
		setEditorOpen(false);
		setSelectedPayer(null);
	};

	const handleOpenDeleteDialog = (payer: PayerResponse) => {
		setSelectedPayer(payer);
		setDeleteDialogOpen(true);
	};

	const handleCloseDeleteDialog = () => {
		setDeleteDialogOpen(false);
		setSelectedPayer(null);
	};

	const handleSubmit = async (data: CreatePayer) => {
		try {
			if (selectedPayer) {
				// SIMS-8666 AC3 — capture the status transition BEFORE the save, while the old
				// value is still known.
				const statusChanged = data.status !== selectedPayer.status;

				await updateMutation.mutateAsync({
					...data,
					_id: selectedPayer._id,
					updatedBy: userInfo?.id || "",
				});

				// Every audit call sits AFTER the await on purpose: a rejected save throws here,
				// so nothing is recorded for a change that did not happen (TestRail C17502).
				await postAuditlog({
					action: "When updating",
					actionDetail: `Updated payer: ${data.name}`,
					actionCode: "WEB_PAYER_UPD",
				});

				// AC3 lists "created, edited, made inactive and deleted" as FOUR separate kinds.
				// A deactivation used to be filed as WEB_PAYER_UPD, indistinguishable from a
				// rename — so the log could not answer "who closed this payer to new business",
				// which is the question an administrator actually gets asked. Recorded in
				// ADDITION to the edit entry, not instead of it: a status change is not a
				// footnote to a rename, and a save can legitimately do both.
				if (statusChanged) {
					await postAuditlog({
						action: "When changing status",
						actionDetail: `Payer status changed: ${data.name} — ${selectedPayer.status} to ${data.status}`,
						actionCode: "WEB_PAYER_STATUS_CHG",
					});
				}

				showSnackBar("Payer updated successfully", "success");
			} else {
				await createMutation.mutateAsync({
					...data,
					createdBy: userInfo?.id || "",
				});
				await postAuditlog({
					action: "When creating",
					actionDetail: `Created new payer: ${data.name}`,
					actionCode: "WEB_PAYER_ADD",
				});
				showSnackBar("Payer created successfully", "success");
			}
			handleCloseDialog();
		} catch (error: unknown) {
			const apiError = error as ApiError;
			const errorMessage =
				apiError?.response?.data?.message ||
				apiError?.message ||
				`Failed to ${selectedPayer ? "update" : "create"} payer`;
			const message = Array.isArray(errorMessage)
				? errorMessage.join(", ")
				: errorMessage;
			// NO snackbar here. The editor shows a persistent banner above the form
			// (ConfigEditorShell `saveError`), and the design uses its toast for SUCCESS only —
			// `flash()` is called on save and delete, never on a failure, which sets `saveError`
			// alone (Payers Pattern v2 L858-869).
			//
			// An earlier note here claimed the duplication was intentional, reasoning that the
			// snackbar was transient while the banner persisted. That was written when this screen
			// was a MODAL with no banner of its own; once the full-page editor gained one, the
			// snackbar became a second copy of the same sentence — announced twice to a screen
			// reader, and inviting the reader to wonder whether two things went wrong.
			//
			// SIMS-8614 AC3/AC4: still RETHROW. The shell decides dirty state from whether onSave
			// resolved, so swallowing would make it treat a FAILED save as saved — the pill would
			// clear, the editor would close, and the user's input would be gone.
			throw new Error(message);
		}
	};

	const handleDelete = async () => {
		if (!selectedPayer) return;

		try {
			await deleteMutation.mutateAsync({
				_id: selectedPayer._id,
				updatedBy: userInfo?.id || "",
			});
			await postAuditlog({
				action: "When deleting",
				actionDetail: `Deleted payer: ${selectedPayer.name}`,
				actionCode: "WEB_PAYER_DELETE",
			});
			showSnackBar("Payer deleted successfully", "success");
			handleCloseDeleteDialog();
			// Deleted FROM the editor: the record it is editing is gone, so there is nothing to
			// return to. Close it and land back on the list. No discard prompt — the unsaved
			// changes belong to a payer that no longer exists, so asking whether to keep them
			// would be a question with no meaningful answer.
			if (editorOpen) {
				setEditorOpen(false);
			}
		} catch (error: unknown) {
			const apiError = error as ApiError;
			const errorMessage =
				apiError?.response?.data?.message ||
				apiError?.message ||
				"Failed to delete payer";
			const message = Array.isArray(errorMessage)
				? errorMessage.join(", ")
				: errorMessage;
			showSnackBar(message, "error");
		}
	};

	if (editorOpen) {
		return (
			// The delete confirmation is rendered HERE as well as below: this branch returns early,
			// so the copy further down is unreachable while the editor is open, and the editor's
			// Delete button would open a dialog that never appears.
			<>
				<PayerEditor
					payer={selectedPayer}
					onSubmit={handleSubmit}
					onExit={handleCloseDialog}
					// Design L405-410 — deleting from the editor opens the SAME confirmation the list
					// uses, so the dependency counts and the refusal apply identically. selectedPayer
					// is already the record being edited, so no re-selection is needed.
					onDelete={selectedPayer ? () => setDeleteDialogOpen(true) : undefined}
				/>
				<ConfigDeleteDialog
					open={deleteDialogOpen}
					recordName={selectedPayer?.name ?? ""}
					recordNoun="payer"
					dependencies={deleteDependencies}
					isCheckingDependencies={isCheckingDependencies}
					dependenciesUnavailable={dependenciesUnavailable}
					onConfirm={handleDelete}
					onClose={handleCloseDeleteDialog}
					isDeleting={deleteMutation.isPending}
				/>
			</>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				// SPACING IS THE DESIGN'S, EXACTLY. The Layout's <Main> pads 20px on every side
				// (views/Layout/index.tsx:56), so that padding is cancelled here with a negative
				// margin and the mock's own values are applied instead:
				//   header      20px 24px 12px  (L88)
				//   filter row  0 24px 16px     (L115)
				//   table frame margin 0 24px 24px (L230)
				// Matching the sibling config screens (Cups:194 `p: 3`) was the wrong instinct —
				// those screens are the OLD look this epic replaces, so "consistent with them"
				// meant consistent with what is being redesigned. Actions and Cups & Analytes will
				// adopt these gutters when they land, and the shared shell is where that becomes
				// one decision rather than three.
				m: "-20px",
				// <Main> also owns the page height (marginTop APP_BAR_HEIGHT=82 + minHeight
				// 100vh-82). Cancelling its padding means reclaiming that 40px of vertical too, so
				// the column is sized off the same basis rather than declaring its own 100vh.
				minHeight: "calc(100vh - 82px)",
			}}
		>
			{/* Page header (design L88-113): a three-line stack — eyebrow, title, count — with the
			    view toggle, rows-per-page, audit log and Add right-aligned on the SAME row. The
			    previous layout stacked the toggle above the search field, which is what made the
			    control positions differ from the mock. */}
			<Box
				display="flex"
				alignItems="flex-end"
				gap={2}
				sx={{ px: 3, pt: 2.5, pb: 1.5 }}
			>
				<Box>
					<Typography
						sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 0.5 }}
					>
						Program setup
					</Typography>
					<Typography
						component="h1"
						sx={{ fontSize: "1.5rem", lineHeight: "2rem", m: 0 }}
					>
						Payers
					</Typography>
					{/* countLabel (L894) is "N payers · M members" in the design. The member total
					    is the same number that does not exist yet (SIMS-8647 AC2 / the Members
					    column need the D-3 endpoint), so only the payer count is shown rather
					    than a fabricated one. */}
					<Typography
						sx={{ fontSize: "0.8125rem", color: "text.secondary", mt: "2px" }}
						data-testid="payers-count-label"
					>
						{`${list.grandTotal} ${list.grandTotal === 1 ? "payer" : "payers"}`}
					</Typography>
				</Box>

				<Box sx={{ flex: 1 }} />

				{/* Design v2 keeps only the view toggle on the header row; rows-per-page moved
				    into the pager (L280-288). */}
				<ConfigListViewControls
					view={list.view}
					onViewChange={list.setView}
					showPageSize={false}
					pageSize={list.pageSize}
					onPageSizeChange={list.setPageSize}
					pageSizeOptions={list.pageSizeOptions}
				/>

				{/*
				 * The audit control is `.c-btn.c-btn--secondary` at 40x40 (design L105).
				 * From card-system.css:336-347 that resolves to: min-height 40px,
				 * border-radius var(--radius-md) = 8px, background --surface-0,
				 * colour AND border --brand-primary-main, hover --surface-brand-soft,
				 * with an 18-20px glyph.
				 *
				 * It was bordered with `palette.outline.dark`, which renders LIME on this
				 * tenant — the design's secondary button is outlined in the BRAND colour.
				 */}
				<Tooltip title="Audit log">
					<IconButton
						aria-label="Audit log"
						onClick={() => setOpenAuditLogModal(true)}
						sx={{
							width: "2.5rem",
							height: "2.5rem",
							borderRadius: "0.5rem",
							border: "1px solid",
							borderColor: "primary.main",
							color: "primary.main",
							backgroundColor: "background.paper",
							"&:hover": {
								backgroundColor: (t) => alpha(t.palette.primary.main, 0.06),
							},
						}}
					>
						<HistoryOutlinedIcon sx={{ fontSize: "1.25rem" }} />
					</IconButton>
				</Tooltip>
				{/* `.c-btn.c-btn--primary` (card-system.css:338/344): brand background, white,
				    UPPERCASE with .04em tracking, min-height 40px, 8px radius, 6px gap. */}
				<Button
					variant="contained"
					color="primary"
					startIcon={<AddIcon />}
					onClick={() => handleOpenDialog()}
					sx={C_BTN}
				>
					Add payer
				</Button>
			</Box>

			{/* Search + filters on their own row (L115-122), with the filter summary alongside
			    (L136) — distinct from the pager's range label inside the frame. AC2's "true total
			    of matches" is this one. */}
			<Box
				sx={{
					px: 3,
					pb: 2,
					display: "flex",
					alignItems: "center",
					gap: 1.5,
					flexWrap: "wrap",
				}}
			>
				<ConfigListToolbar
					query={list.query}
					onQueryChange={list.setQuery}
					searchPlaceholder="Search payers by name, description or pattern"
					status={list.status}
					onStatusChange={list.setStatus}
					statusOptions={STATUS_OPTIONS}
					categoryLabel="Payer category"
					category={list.category}
					onCategoryChange={list.setCategory}
					categoryOptions={filterableCategories(categoryItems).map((c) => ({
						value: c._id,
						label: payerCategoryLabel(c),
					}))}
				/>
				{/* Design L135-136: a flex:1 spacer pushes resultLabel to the RIGHT edge of the
				    filter row. It was sitting immediately after the chips. */}
				<Box sx={{ flex: 1 }} />
				{/* ALWAYS shown, changing form rather than appearing (design L948-950): the plain
				    count while unnarrowed, "Showing N of M" once a search or filter narrows it.
				    Hiding it when unnarrowed made the row jump as you typed.
				    Distinct from the header's countLabel (L92), which states the whole catalog. */}
				<Typography
					sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
					data-testid="payers-filter-summary"
				>
					{list.isNarrowed
						? `Showing ${list.total} of ${list.grandTotal}`
						: `${list.grandTotal} ${list.grandTotal === 1 ? "payer" : "payers"}`}
				</Typography>
			</Box>

			{/* The frame wraps the TABLE ONLY (L230: margin 0 24px 24px, --surface-0, 1px
			    --border-default, 12px radius, overflow hidden). The toolbar sits on the page
			    background above it — the previous version put everything inside one Card, which
			    is the "container is different" difference. */}
			<Box
				sx={{
					// Design L230: the frame's own `margin: 0 24px 24px`.
					px: 3,
					pb: 0,
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
					"& > *": { mb: 3 },
				}}
			>
				{listState !== "ready" ? (
					<ConfigListStates
						state={listState}
						view={list.view}
						copy={stateCopy}
						query={list.query}
						errorMessage={payersError?.message}
						onRetry={() => refetchPayers()}
						onClearSearch={() => list.setQuery("")}
						onClearAllFilters={() => {
							list.setQuery("");
							list.setStatus("all");
						}}
						onAdd={() => handleOpenDialog()}
					/>
				) : list.view === "table" ? (
					/* TABLE: a white frame (L230) with the rows scrolling inside and the pager at
					   its foot on --surface-0 (L280). */
					<Box
						sx={{
							flex: 1,
							minHeight: 0,
							display: "flex",
							flexDirection: "column",
							backgroundColor: "background.paper",
							border: "1px solid",
							borderColor: "divider",
							borderRadius: "0.75rem",
							overflow: "hidden",
						}}
					>
						<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
							<ConfigTable<PayerResponse>
								ariaLabel="Payers"
								rows={list.visible}
								columns={columns}
								rowKey={(payer) => payer._id}
								sortKey={list.sortKey}
								sortDir={list.sortDir}
								onSort={list.toggleSort}
							/>
						</Box>
						{renderPager({ inFrame: true })}
					</Box>
				) : (
					/* CARDS: NO frame (L305-306). The scroll area is transparent — the cards are
					   the only surfaces — and the pager sits on the page tint below it, separated
					   by a top border rather than enclosed. Wrapping cards in the table's white
					   frame put a second surface behind them, which is what read as wrong. */
					<Box
						sx={{
							flex: 1,
							minHeight: 0,
							display: "flex",
							flexDirection: "column",
						}}
					>
						<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
							<ConfigCards<PayerResponse>
								rows={list.visible}
								rowKey={(payer) => payer._id}
								title={(payer) => payer.name}
								subtitle={(payer) => payer.regularExpression}
								showIcon
								onRowClick={handleOpenDialog}
								// Status pill AND the row kebab (design L317-330). The card view shipped
								// with only the pill, so Edit and Delete were unreachable without
								// switching back to the table — AC1 requires the two views to be
								// interchangeable over the same records, not for one to be read-only.
								trailing={(payer) => (
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										{statusChip(payer)}
										<ConfigRowActions
											recordName={payer.name}
											actions={[
												{
													label: "Edit payer",
													icon: <EditIcon sx={{ fontSize: "1.125rem" }} />,
													onClick: () => handleOpenDialog(payer),
												},
												{
													label: "Delete",
													icon: <DeleteIcon sx={{ fontSize: "1.125rem" }} />,
													onClick: () => handleOpenDeleteDialog(payer),
													destructive: true,
												},
											]}
										/>
									</Box>
								)}
								description={(payer) => payer.description}
								// Design L334-346 puts THREE stats here: Members, Member IDs and
								// ID pattern. Members needs a count the payer model does not carry
								// (the D-3 endpoint — the same gap as table column 4 and SIMS-8647
								// AC2), so it is omitted rather than shown as 0, which would read
								// as "no members". The slot takes it with no rework.
								body={(payer) => {
									const chips = payerCategoryChips(payer);
									return chips.length === 0 ? null : (
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
											{chips.map((cat) => (
												<Box
													key={cat._id}
													component="span"
													sx={{
														fontSize: "0.6875rem",
														fontWeight: 500,
														padding: "3px 8px",
														borderRadius: "999px",
														backgroundColor: (t) => t.palette.surfaceTwo.main,
														color: "text.secondary",
														border: "1px",
														borderStyle:
															cat.status === "INACTIVE" ? "dashed" : "solid",
														borderColor: "divider",
														opacity: cat.status === "INACTIVE" ? 0.6 : 1,
													}}
												>
													{payerCategoryLabel(cat)}
												</Box>
											))}
										</Box>
									);
								}}
								stats={(payer) => [
									{
										label: "Member IDs",
										value:
											payer.autogenerated === "Y" ? "Autogenerated" : "Manual",
									},
									{
										label: "ID pattern",
										value: payer.regularExpression,
										mono: true,
									},
								]}
							/>
						</Box>
						{renderPager({ inFrame: false })}
					</Box>
				)}
			</Box>

			<ConfigDeleteDialog
				open={deleteDialogOpen}
				recordName={selectedPayer?.name ?? ""}
				recordNoun="payer"
				dependencies={deleteDependencies}
				isCheckingDependencies={isCheckingDependencies}
				dependenciesUnavailable={dependenciesUnavailable}
				onConfirm={handleDelete}
				onClose={handleCloseDeleteDialog}
				isDeleting={deleteMutation.isPending}
			/>

			{openAuditLogModal ? (
				<AuditLogModal
					open={openAuditLogModal}
					module="Payers"
					onClose={() => setOpenAuditLogModal(false)}
				/>
			) : null}
		</Box>
	);
};

export default PayersComponent;
